import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { initSyncService, addSyncListener } from '../lib/syncService';
import { OnboardingTour } from './OnboardingTour';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { LOGO_MARK } from '../lib/brand';
import NotificationBell from './NotificationBell';
import { AIStatusBadge } from './AIStatusBadge';
import { AIQuotaNudge } from './AIQuotaNudge';
import DesktopVersionChip from './DesktopVersionChip';
import ConnectionStatusChip from './ConnectionStatusChip';
import { ShiftLockOverlay } from './ShiftLockOverlay';
import { ShiftHandoverDialog } from './shift/ShiftHandoverDialog';
import { ThemeToggle } from './layout/ThemeToggle';
import { BranchSwitcher } from './layout/BranchSwitcher';
import { SidebarNav } from './layout/SidebarNav';
import { AIAssistantBubble } from './AIAssistantBubble';
import { useDutiVisibility } from '../lib/useDutiVisibility';
import { ROLE_LABELS, hasAccess } from '../lib/navConfig';
import { DemoBanner, DEMO_TENANT_ID } from './DemoBanner';
import { StaffAppInstallDialog } from './StaffAppInstallDialog';
import { WhatsNewDialog } from './WhatsNewDialog';
import {
  Menu, LogOut, User, Wifi, WifiOff, RefreshCw, Lock, Loader2,
  ChevronDown, Shield, AlertTriangle, ClipboardList, Smartphone, Eye, EyeOff,
} from 'lucide-react';

// Nav config, RBAC, and sub-components extracted to:
//   src/lib/navConfig.js      — ROLE_PERMISSIONS, ROLE_LABELS, NAV_GROUPS, hasAccess
//   src/components/layout/ThemeToggle.js
//   src/components/layout/BranchSwitcher.js
//   src/components/layout/SidebarNav.js

export default function Layout({ children }) {
  const { user, logout, exitImpersonation, locked, lockShift } = useAuth();
  const { settings } = useSettings();
  const location = useLocation();
  const isPOS = location.pathname === '/pos';
  const isMigration = ['/bulk-import', '/suppliers/import', '/patients/import', '/patient-invitations', '/patient-invitations/policies'].includes(location.pathname);
  const [dutiVisible, setDutiVisible] = useDutiVisibility(user?.user_id);
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOnlineBanner, setShowOnlineBanner] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const syncInitialized = useRef(false);
  const [expiryAlerts, setExpiryAlerts] = useState([]);
  const [showExpiryDialog, setShowExpiryDialog] = useState(false);
  const expiryChecked = useRef(false);
  const [showStockTakeReminder, setShowStockTakeReminder] = useState(false);
  const [showHandover, setShowHandover] = useState(false);
  const [showStaffApp, setShowStaffApp] = useState(false);
  const [stockTakeDays, setStockTakeDays] = useState(null);
  const [endingSupport, setEndingSupport] = useState(false);
  const stockTakeChecked = useRef(false);
  const [whatsappUnread, setWhatsappUnread] = useState(0);

  const moduleToggles = settings?.module_toggles || {};
  const isCareWorkspace = location.pathname.startsWith('/customers/');

  // Daily expiry alerts check
  useEffect(() => {
    if (!user || expiryChecked.current) return;
    expiryChecked.current = true;
    const lastDismissed = localStorage.getItem('pharmxai_expiry_alert_date');
    const today = new Date().toISOString().slice(0, 10);
    if (lastDismissed === today) return;
    api.get('/expired-products/alerts').then(res => {
      if (res.data.count > 0) {
        setExpiryAlerts(res.data.alerts || []);
        setShowExpiryDialog(true);
      }
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (isCareWorkspace) setShowExpiryDialog(false);
  }, [isCareWorkspace]);

  // Stock take reminder check
  useEffect(() => {
    if (!user || stockTakeChecked.current) return;
    stockTakeChecked.current = true;
    const lastDismissedST = localStorage.getItem('pharmxai_stocktake_reminder_date');
    const todayST = new Date().toISOString().slice(0, 10);
    if (lastDismissedST === todayST) return;
    api.get('/stocktake/status').then(res => {
      if (res.data.is_due && !res.data.active_session) {
        setStockTakeDays(res.data.days_since_last);
        setShowStockTakeReminder(true);
      }
    }).catch(() => {});
  }, [user]);

  // WhatsApp unread count — polled so the sidebar badge stays live without
  // needing the Inbox page open. Skipped entirely for roles/modules that
  // can't see the Inbox item, and paused while the tab is hidden.
  useEffect(() => {
    if (!user || !hasAccess(user.role, 'patients') || moduleToggles.customer_engagement === false) return undefined;
    let cancelled = false;
    const poll = () => {
      if (document.hidden) return;
      api.get('/whatsapp/unread-count').then((r) => {
        if (!cancelled) setWhatsappUnread(r.data.unread || 0);
      }).catch(() => {});
    };
    poll();
    const timer = setInterval(poll, 20000);
    window.addEventListener('whatsapp:unread-changed', poll);
    document.addEventListener('visibilitychange', poll);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('whatsapp:unread-changed', poll);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [user, moduleToggles.customer_engagement]);

  useEffect(() => {
    if (syncInitialized.current) return;
    syncInitialized.current = true;

    const init = async () => {
      try {
        await initSyncService();
      } catch (e) {
        console.error('Sync service init failed:', e);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const unsub = addSyncListener((event, data) => {
      if (event === 'sales_sync_complete' && data?.synced > 0) {
        toast.success(`Synced ${data.synced} offline sale(s)`);
      }
      if (event === 'recall_returns_synced') {
        if (data?.synced) toast.success(`Synced ${data.synced} offline recall return(s)`);
        if (data?.failed) toast.error(`${data.failed} offline recall return(s) were rejected — open the product to review`);
      }
      if (event === 'online') {
        setIsOnline(true);
        setShowOnlineBanner(true);
        setTimeout(() => setShowOnlineBanner(false), 3000);
      }
      if (event === 'offline') setIsOnline(false);
    });

    return () => unsub();
  }, []);

  const handleLogout = async () => {
    navigate(await logout());
  };

  // Auto-lock: cover the till after the pharmacy's configured idle window.
  // 0 (or no PIN) means manual locking only. Independent of the 8-hour
  // desktop idle rule, which signs the cashier out entirely.
  const lockMinutes = Number(settings?.shift_lock_minutes ?? 0);
  useEffect(() => {
    if (!user?.pin_set || !lockMinutes || locked) return undefined;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const events = ['mousedown', 'keydown', 'touchstart', 'wheel'];
    events.forEach((evt) => window.addEventListener(evt, bump, { passive: true }));
    const timer = setInterval(() => {
      if (Date.now() - last > lockMinutes * 60 * 1000) lockShift();
    }, 15000);
    return () => {
      clearInterval(timer);
      events.forEach((evt) => window.removeEventListener(evt, bump));
    };
  }, [user?.pin_set, lockMinutes, locked, lockShift]);

  return (
    <div className={`min-h-screen bg-background flex ${isPOS ? 'pos-app-layout' : ''}`} data-testid="app-layout">
      <DemoBanner
        show={user?.tenant_id === DEMO_TENANT_ID}
        onSignup={async () => { await logout(); navigate('/signup/pharmacy'); }}
      />
      {user?.impersonation && (
        <div className="support-session-banner" data-testid="impersonation-active-banner">
          <div><Shield className="h-4 w-4" /><span><b>Read-only support view</b> · {user.tenant_name || 'Pharmacy'} · ends {new Date(user.impersonation.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span></div>
          <Button size="sm" variant="outline" disabled={endingSupport} onClick={async () => { setEndingSupport(true); try { await exitImpersonation(); } catch { toast.error('Could not exit support view'); setEndingSupport(false); } }} data-testid="exit-impersonation-btn">{endingSupport && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}Exit support view</Button>
        </div>
      )}
      {user?.remote_access && (
        <div className="support-session-banner" data-testid="remote-access-banner">
          <div><Wifi className="h-4 w-4" />
            <span>
              {user.remote_readonly
                ? <><b>Remote access — read only</b> · {user.tenant_name || 'Branch'} · visit the branch's desktop app to make changes</>
                : <><b>Remote access — full access</b> · {user.tenant_name || 'Branch'}</>}
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={handleLogout} data-testid="remote-access-sign-out-btn">Sign out</Button>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-[260px] border-r border-border/60 bg-card fixed inset-y-0 left-0 z-30" data-testid="sidebar">
        <div className="p-5 border-b border-border/60">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <img src={LOGO_MARK} alt="" aria-hidden="true" className="h-8 w-8 rounded-lg object-cover" />
            <span className="leading-none" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="app-wordmark">
              <span className="block text-lg font-bold tracking-tight">DutiLyne</span>
              <span className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Pharmacy POS</span>
            </span>
          </Link>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-3 px-2 custom-scrollbar" data-testid="sidebar-scroll">
          <SidebarNav userRole={user?.role} moduleToggles={moduleToggles} unreadByPath={{ '/whatsapp-inbox': whatsappUnread }} />
        </div>
        <div className="p-3 border-t border-border/60">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
              <Badge variant="outline" className={`text-[10px] mt-0.5 ${ROLE_LABELS[user?.role]?.color || 'bg-gray-500'} text-white border-0`}>
                {ROLE_LABELS[user?.role]?.label || user?.role || 'User'}
              </Badge>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 min-w-0 w-full lg:ml-[260px] flex flex-col ${isPOS ? 'pos-layout-content' : 'min-h-screen'}`}>
        {!isOnline && <div className="bg-destructive text-destructive-foreground flex items-center justify-center gap-2 px-4 py-2 text-xs shrink-0" data-testid="app-offline-banner" role="status">
          <WifiOff className="h-3.5 w-3.5 shrink-0" /> <span>Working offline — sales sync when connected.</span>
        </div>}
        {showOnlineBanner && <div className="bg-primary text-primary-foreground flex items-center justify-center gap-2 px-4 py-2 text-xs shrink-0" data-testid="app-online-banner" role="status">
          <Wifi className="h-3.5 w-3.5" /> Back online!
        </div>}
        <header className="sticky top-0 z-20 bg-card border-b border-border min-h-14 py-2 shrink-0 flex items-center px-4 lg:px-6 gap-2 sm:gap-3" data-testid="top-bar">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden h-9 w-9 shrink-0" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[260px] p-0 flex flex-col">
              <div className="p-5 border-b border-border/60 shrink-0">
                <div className="flex items-center gap-2.5">
                  <img src={LOGO_MARK} alt="" aria-hidden="true" className="h-8 w-8 rounded-lg object-cover" />
                  <span className="leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <span className="block text-lg font-bold tracking-tight">DutiLyne</span>
                    <span className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Pharmacy POS</span>
                  </span>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain custom-scrollbar" data-testid="mobile-sidebar-scroll">
                <SidebarNav onNavigate={() => setMobileOpen(false)} userRole={user?.role} moduleToggles={moduleToggles} unreadByPath={{ '/whatsapp-inbox': whatsappUnread }} />
              </div>
            </SheetContent>
          </Sheet>

          <div className="hidden lg:block flex-1" />

          <div className="flex flex-1 lg:flex-none flex-wrap items-center justify-end gap-1.5 sm:gap-2 min-w-0">
            <BranchSwitcher />
            <ConnectionStatusChip />
            <NotificationBell />
            <AIStatusBadge />
            <div className="hidden md:block"><DesktopVersionChip canManageChannel={user?.role === 'super_admin' || user?.role === 'admin'} /></div>
            <div className="hidden sm:block"><ThemeToggle /></div>
            {user?.pin_set && (
              <Button
                variant="ghost" size="icon" className="h-9 w-9" title="Lock till (PIN to unlock)"
                onClick={() => setShowHandover(true)} data-testid="lock-till-btn"
              >
                <Lock className="h-4 w-4" />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full" data-testid="user-menu">
                  <User className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => isMigration ? navigate('/duti-ai') : setDutiVisible(!dutiVisible)} data-testid="duti-visibility-toggle">
                  {dutiVisible && !isMigration ? <EyeOff className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
                  {isMigration ? 'Open Duti-AI' : dutiVisible ? 'Hide Duti-AI' : 'Show Duti-AI'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowStaffApp(true)} data-testid="staff-app-install-btn">
                  <Smartphone className="mr-2 h-4 w-4" /> Staff mobile app
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <AIQuotaNudge />

        <main className={`flex-1 p-4 lg:p-6 ${isPOS ? 'pos-layout-main min-h-0' : ''}`}>
          {children}
        </main>
      </div>
      {locked && <ShiftLockOverlay />}
      {!locked && <WhatsNewDialog />}
      <ShiftHandoverDialog
        open={showHandover}
        onClose={() => setShowHandover(false)}
        onDone={() => { setShowHandover(false); lockShift(); }}
      />
      <StaffAppInstallDialog open={showStaffApp} onOpenChange={setShowStaffApp} />
      <Toaster position="top-right" richColors />

      {/* Expiry Alert Dialog */}
      {!isCareWorkspace && showExpiryDialog && expiryAlerts.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 pointer-events-none" data-testid="expiry-alert-dialog">
          <div className="bg-card border rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col pointer-events-auto">
            <div className="p-5 border-b flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Expired Items Alert</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {expiryAlerts.length} expired batch{expiryAlerts.length > 1 ? 'es' : ''} still on shelves. Remove immediately to prevent accidental sale.
                </p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {expiryAlerts.slice(0, 15).map((a, i) => (
                <div key={`${a.product_id || a.product_name}-${a.batch_number || i}`} className="flex items-center justify-between bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
                  <div>
                    <p className="text-sm font-medium">{a.product_name}</p>
                    <p className="text-xs text-muted-foreground">Batch: {a.batch_number} | Exp: {a.expiry_date} | Qty: {a.quantity}</p>
                  </div>
                </div>
              ))}
              {expiryAlerts.length > 15 && (
                <p className="text-xs text-muted-foreground text-center">...and {expiryAlerts.length - 15} more</p>
              )}
            </div>
            <div className="p-4 border-t flex gap-3">
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => { setShowExpiryDialog(false); navigate('/expired-products'); }}
                data-testid="expiry-alert-manage-btn"
              >
                Manage Expired Items
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowExpiryDialog(false);
                  localStorage.setItem('pharmxai_expiry_alert_date', new Date().toISOString().slice(0, 10));
                }}
                data-testid="expiry-alert-dismiss-btn"
              >
                Dismiss for Today
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Take Reminder */}
      {showStockTakeReminder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="stocktake-reminder-dialog">
          <div className="bg-background rounded-lg p-6 max-w-md w-full shadow-xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <ClipboardList className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Stock Take Due</h3>
                <p className="text-sm text-muted-foreground">
                  {stockTakeDays !== null ? `${stockTakeDays} days since last stock take.` : 'No stock take on record.'} Time for a physical inventory count.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => { setShowStockTakeReminder(false); navigate('/stock-take'); }}
                className="gap-1.5"
                data-testid="stocktake-start-btn"
              >
                <ClipboardList className="h-4 w-4" />
                Start Stock Take
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowStockTakeReminder(false);
                  localStorage.setItem('pharmxai_stocktake_reminder_date', new Date().toISOString().slice(0, 10));
                }}
                data-testid="stocktake-remind-dismiss-btn"
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}
      <OnboardingTour />
      {user && !locked && dutiVisible && !isMigration && <AIAssistantBubble onHide={() => setDutiVisible(false)} />}
    </div>
  );
}
